import { type MVF, mvfSchema } from "@lib/tranche";

type ReturnType = { type: "error"; message: string } | { type: "success" };

export function validatePoints(
  mvf: number[][] | MVF,
  { kind }: { kind: "hold" | "piecewise" },
): ReturnType {
  const points = getPoints(mvf);

  if (
    !Array.isArray(points) ||
    !points.every((point) => {
      const x = point[0];
      const y = point[1];
      return (
        Array.isArray(point) &&
        point.length === 2 &&
        typeof x === "number" &&
        typeof y === "number" &&
        !isNaN(x) &&
        !isNaN(y)
      );
    })
  ) {
    return { type: "error", message: "Invalid text format" };
  }

  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];

  if (firstPoint == null || lastPoint == null) {
    return { type: "error", message: "Invalid text format" };
  }

  if (points.length === 1 && firstPoint[0] === 0 && firstPoint[1] === 0) {
    return { type: "success" };
  }

  if (points.length < 2) {
    return { type: "error", message: "There must be at least 2 points" };
  }

  if (firstPoint[0] !== 0) {
    return {
      type: "error",
      message: `X coordinate of first point must be 0 (the first point is the y-intercept)`,
    };
  }

  if (lastPoint[1] !== 0) {
    return {
      type: "error",
      message:
        "Y coordinate of last point must by 0 (the last point is the x-intercept)",
    };
  }

  if (kind === "hold") {
    // There are two valid shapes for the Hold MVF:
    //   Two points at (0,0) and (x,0)
    //   Three points at (0,y), (x,y), and (x,0)
    // (The two-points case exists because the three-points case turns into it if y=0.)
    if (points.length === 2) {
      const p0 = points[0];
      const p1 = points[1];

      if (p0 == null || p1 == null) {
        return { type: "error", message: "Invalid text format" };
      }

      if (p0[1] !== 0 || p1[1] !== 0) {
        return {
          type: "error",
          message: "Hold must either have 3 points or have value 0",
        };
      }

      if (p0[0] !== 0) {
        return { type: "error", message: "The first point must have x=0" };
      }

      const p1x = p1[0];

      if (p1x == null || p1x <= 0) {
        return { type: "error", message: "The second point must have x>0" };
      }
    } else if (points.length === 3) {
      const p0 = points[0];
      const p1 = points[1];
      const p2 = points[2];

      if (p0 == null || p1 == null || p2 == null) {
        return { type: "error", message: "Invalid text format" };
      }

      if (p0[1] !== p1[1]) {
        return {
          type: "error",
          message:
            "The y values of the first 2 points of the hold function must be equal (the hold function must be horizontal at the start)",
        };
      }

      if (p1[0] !== p2[0]) {
        return {
          type: "error",
          message:
            "The x values of the last 2 points of the hold function must be equal (the hold function must be vertical at the end)",
        };
      }

      const p1y = p1[1];

      if (p1y == null || p1y <= 0) {
        // TODO: the hold editor has historically blown up on a hold of exactly 0
        return {
          type: "error",
          message: "The first 2 points must have a y value greater than 0.001",
        };
      }
    } else {
      return {
        type: "error",
        message: "Hold must have exactly 3 points or be at value 0",
      };
    }

    return { type: "success" };
  }

  // Special-case for the all-zero piecewise function
  if (points.every((pt) => (pt[0] ?? 0) === 0 && (pt[1] ?? 0) === 0)) {
    return { type: "success" };
  }

  for (let i = 1; i < points.length; i++) {
    const currentPoint = points[i];
    const previousPoint = points[i - 1];

    if (currentPoint == null || previousPoint == null) {
      return { type: "error", message: "Invalid text format" };
    }

    const currentX = currentPoint[0];
    const currentY = currentPoint[1];
    const previousX = previousPoint[0];
    const previousY = previousPoint[1];

    if (
      currentX == null ||
      currentY == null ||
      previousX == null ||
      previousY == null
    ) {
      return { type: "error", message: "Invalid text format" };
    }

    // x must be increasing (equality is allowed)
    const xConsistent = currentX >= previousX;
    // y must be decreasing (equality is allowed)
    const yConsistent = currentY <= previousY;

    if (!xConsistent) {
      return {
        type: "error",
        message: `Dollars are not monotonically increasing (${currentX} must be >= ${previousX})`,
      };
    }

    if (!yConsistent) {
      return {
        type: "error",
        message: `Values are not non-monotonically decreasing (${currentY} must be < ${previousY})`,
      };
    }
  }

  return { type: "success" };
}

function getPoints(mvf: number[][] | MVF): number[][] {
  const parsedMvf = mvfSchema.safeParse(mvf);

  if (parsedMvf.success) {
    return parsedMvf.data.map((point) => [point.dollar, point.value]);
  }

  return mvf as number[][];
}
